import type { Form as UseFormResult } from '@jotta/hooks'
import { useForm } from '@jotta/hooks'
import { PhoneInput, validatePhoneNumber } from '@jotta/ui/PhoneInput'
import type { CheckPhoneNumberResponse } from '@jotta/grpc-connect-openapi/signup'
import { FormField } from '@jotta/ui/FormField'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { CreateUser } from './CreateUser'
import { Form } from '@jotta/ui/Form'
import { t, Trans } from '@lingui/macro'
import { PasswordInput } from '@jotta/ui/PasswordInput'
import { CTAButton } from './CTAButton'
import { phoneNumberLogin as phoneNumberLoginDescriptor } from '@jotta/grpc-connect-openapi/loginQuery'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import { getCurrentCountry } from '@jotta/grpc-connect-openapi/geoQuery'
import {
  checkPhoneNumber as checkPhoneNumberDescriptor,
  verifyPreSignup as verifyPreSignupDescriptor,
} from '@jotta/grpc-connect-openapi/signupQuery'
import { ChallengeDialog } from '@jotta/ui/ChallengeDialog'
import { useCallback, useState } from 'react'
import type { PartialMessage } from '@bufbuild/protobuf'
import type { Credentials } from '@jotta/grpc-connect-openapi/credentials'
import { Challenge } from '@jotta/grpc-connect-openapi/credentials'
import { Redirect } from '@jotta/ui/Redirect'
import type { PhoneNumberLoginResponse } from '@jotta/grpc-connect-openapi/login'
import { PhoneNumberLoginRequest } from '@jotta/grpc-connect-openapi/login'
import { useBrandingInfo } from '@jotta/grpc-connect-client/config'
import { FormFieldErrorMessage, Label } from '@jotta/ui/Label'
import { generateSignupToken } from './signupForm'
import { LoginExpired } from './LoginExpired'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { ResetPassword } from './ResetPassword'
import { formatPhoneNumber } from '@jotta/utils/formatPhoneNumber'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Divider } from '@jotta/ui/Divider'
import SvgFillCheckCircle from '@jotta/ui/SvgFillCheckCircle'

export function InvalidPreSignup({ onReset }: { onReset: () => void }) {
  const { brandName, customerGroupName, homepageLink } = useBrandingInfo()
  return (
    <div className="flex flex-col gap-4">
      <h2 className="h2">
        ...<Trans>One small detail</Trans>
      </h2>
      <p>
        {customerGroupName && (
          <Trans>We need to link your {customerGroupName} account</Trans>
        )}
      </p>
      <p>
        {customerGroupName && brandName && (
          <Trans>
            Simply go to {customerGroupName} to activate your phone number with{' '}
            {brandName}
          </Trans>
        )}
      </p>
      <a className="btn btn-primary" href={homepageLink}>
        {customerGroupName && <Trans>Go to {customerGroupName}</Trans>}
      </a>
      <button className="btn btn-ghost" onClick={onReset}>
        <Trans>Cancel</Trans>
      </button>
    </div>
  )
}

function PhoneNumber({ phoneNumber }: { phoneNumber: string }) {
  return (
    <>
      <Label label={t`Phone number`} />
      <div className="flex items-center justify-between">
        {formatPhoneNumber(phoneNumber)}
        <PlainBrandIcon
          className="text-good"
          icon={SvgFillCheckCircle}
          height={24}
        />
      </div>

      <Divider className="my-6" />
    </>
  )
}

function LoginForm({
  phoneNumber,
  phoneNumberLoginForm,
  wrongPassword,
}: {
  phoneNumber: string
  phoneNumberLoginForm: UseFormResult<any>
  wrongPassword?: boolean
}) {
  const {
    isSubmitting,
    fields: { password },
  } = phoneNumberLoginForm

  const [forgotPassword, setForgotPassword] = useState(false)
  return (
    <>
      <ModalDialog
        open={forgotPassword}
        className="!px-8 !py-16"
        onClose={() => setForgotPassword(false)}
      >
        <ResetPassword onClose={() => setForgotPassword(false)} />
      </ModalDialog>
      <Form form={phoneNumberLoginForm} className="flex flex-col">
        <PhoneNumber phoneNumber={phoneNumber} />

        <FormField field={password} label={t`Password`}>
          <PasswordInput {...password.inputProps} />
          <button
            type="button"
            className="btn-link mt-2 self-start"
            onClick={event => {
              event.preventDefault()
              setForgotPassword(true)
            }}
          >
            <Trans>Forgot password?</Trans>
          </button>
          {wrongPassword && (
            <FormFieldErrorMessage errorMessage={t`Invalid password`} />
          )}
        </FormField>

        <CTAButton type="submit" loading={isSubmitting}>
          <Trans>Login</Trans>
        </CTAButton>

        <button type="reset" className="btn btn-ghost">
          <Trans>Cancel</Trans>
        </button>
      </Form>
    </>
  )
}

export function PhoneNumberLogin({
  phoneNumber,
  loginToken,
  onReset,
}: {
  phoneNumber: string
  loginToken: string
  onReset: () => void
}) {
  const { brandCode } = useBrandingInfo()
  const [data, setData] = useState<PhoneNumberLoginResponse>()
  const { mutateAsync: phoneNumberLogin } = useMutation(
    phoneNumberLoginDescriptor,
    {
      throwOnError: false,
      onSuccess: setData,
    },
  )

  const submitCredentials = useCallback(
    async (credentials: PartialMessage<Credentials>) =>
      phoneNumberLogin(
        await generateSignupToken(
          new PhoneNumberLoginRequest({
            brandCode,
            loginToken,
            phoneNumber,
            credentials,
          }),
          (message, token) => (message.token = token),
        ),
      ),
    [phoneNumberLogin, brandCode, loginToken, phoneNumber],
  )

  const phoneNumberLoginForm = useForm({
    fields: {
      password: { type: 'text', focusOnMount: true },
    },
    reset: onReset,
    submit: ({ password }) =>
      submitCredentials({
        password,
      }),
  })

  const result = data?.result
  switch (result?.case) {
    case undefined:
      return (
        <LoginForm
          phoneNumber={phoneNumber}
          phoneNumberLoginForm={phoneNumberLoginForm}
        />
      )
    case 'loginFailed':
      return (
        <LoginForm
          phoneNumber={phoneNumber}
          phoneNumberLoginForm={phoneNumberLoginForm}
          wrongPassword
        />
      )
    case 'challenges':
      const challenges = result.value
      return challenges.challenges.includes(Challenge.PASSWORD) ? (
        <LoginForm
          phoneNumber={phoneNumber}
          phoneNumberLoginForm={phoneNumberLoginForm}
          wrongPassword
        />
      ) : (
        <ChallengeDialog
          requestType="positive"
          challenges={challenges}
          onRequest={credentials => submitCredentials(credentials)}
          onClose={() => setData(undefined)}
          defaultCredentials={{
            password: phoneNumberLoginForm.password.value,
          }}
        />
      )
    case 'redirectUrl':
      return <Redirect to={result.value} reload inline />
    case 'loginExpired':
      return <LoginExpired />
    default:
      return exhaustiveGuard(result)
  }
}

export function PreSignupForm({
  loginToken,
  phoneNumber,
  onReset,
}: {
  loginToken: string
  phoneNumber: string
  onReset: () => void
}) {
  return (
    <div>
      <PhoneNumber phoneNumber={phoneNumber} />

      <h1 className="body-lg-ln pb-8">
        <Trans>Sign up now</Trans>
      </h1>

      <CreateUser
        allowAppPromotion={false}
        loginToken={loginToken}
        onClose={onReset}
        type={{
          case: 'presignup',
          phoneNumber: phoneNumber,
        }}
      />
    </div>
  )
}

function PreSignupVerification(props: {
  loginToken: string
  phoneNumber: string
  onReset: () => void
}) {
  const { phoneNumber, onReset } = props
  const { customerGroupCode } = useBrandingInfo()
  const { data, mutateAsync } = useMutation(verifyPreSignupDescriptor)

  const challenges = data?.challenges
  if (challenges?.challenges?.length === 0) {
    return <PreSignupForm {...props} />
  } else {
    return (
      <ChallengeDialog
        requestType="positive"
        onClose={onReset}
        challenges={challenges}
        onRequest={credentials =>
          mutateAsync({
            customerGroupCode,
            credentials,
            phoneNumber,
          })
        }
      />
    )
  }
}

function Switch({
  phoneNumber,
  loginToken,
  onReset,
  checkPhoneNumberResponse,
}: {
  phoneNumber: string
  loginToken: string
  onReset: () => void
  checkPhoneNumberResponse: CheckPhoneNumberResponse
}) {
  const result = checkPhoneNumberResponse.result
  switch (result.case) {
    case 'phoneNumberActive':
      return (
        <PhoneNumberLogin
          phoneNumber={phoneNumber}
          onReset={onReset}
          loginToken={loginToken}
        />
      )
    case 'phoneNumberInPreSignup':
      return (
        <PreSignupVerification
          phoneNumber={phoneNumber}
          onReset={onReset}
          loginToken={loginToken}
        />
      )
    case undefined:
    case 'phoneNumberInvalid':
      return <InvalidPreSignup onReset={onReset} />
    default:
      return exhaustiveGuard(result)
  }
}

export function PreSignup({ loginToken }: { loginToken: string }) {
  const { customerGroupCode } = useBrandingInfo()
  const { data: currentCountry } = useQuery(
    getCurrentCountry,
    {},
    {
      throwOnError: false,
      select: data => data.country,
    },
  )

  const {
    data: checkPhoneNumberResponse,
    mutateAsync: checkPhoneNumber,
    reset,
  } = useMutation(checkPhoneNumberDescriptor)

  const phoneNumberForm = useForm({
    fields: {
      phoneNumber: {
        type: 'text',
        validate: validatePhoneNumber,
        onChange: reset,
      },
    },
    submit: ({ phoneNumber }) =>
      checkPhoneNumber({
        customerGroupCode,
        phoneNumber,
      }),
    reset,
  })

  const {
    onSubmit,
    fields: { phoneNumber },
  } = phoneNumberForm
  return (
    <>
      {!checkPhoneNumberResponse && (
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <FormField field={phoneNumber} label={t`Phone number`}>
            <PhoneInput
              country={currentCountry}
              {...phoneNumber.inputProps}
              autoFocus
            />
          </FormField>

          <button className="btn btn-primary mt-6">
            <Trans>Continue</Trans>
          </button>
        </form>
      )}
      {checkPhoneNumberResponse && (
        <Switch
          phoneNumber={phoneNumber.value}
          loginToken={loginToken}
          onReset={phoneNumberForm.onReset}
          checkPhoneNumberResponse={checkPhoneNumberResponse}
        />
      )}
    </>
  )
}
